import { z } from "zod";

// One list contract for every owner that answers a list a page at a time (ADR-033). A page is
// keyset, never an offset: each row carries a sort key, unique and stable (the sort field as
// text, then the row's id), the cursor is the last key served, and the next page is every row
// strictly after it. A row added or removed between two pages never repeats one or skips one
// that was there all along. The sort field is written as text that orders the same way in a
// byte-wise comparison (`COLLATE "C"` in Postgres, `<` in JavaScript): an ISO date, a lowercase
// name, a zero-padded count. A missing value is the empty text, so it sorts first going up and
// last going down. On the wire the cursor is that key in base64url: opaque, and safe in a URL.

/** How many rows a page holds when the caller does not say, and the most it may ask for. */
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

/** `cursor` is opaque: the previous page's `nextCursor`, never built by the caller. */
export const keysetPageInputSchema = z.object({
  cursor: z.string().min(1).max(2_000).optional(),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE).optional(),
});
export type KeysetPageInput = z.infer<typeof keysetPageInputSchema>;

/** One page: its rows, in the list's own order, and the next page's cursor (null on the last). */
export function keysetPageSchema<TItem extends z.ZodType>(item: TItem) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}
export type KeysetPage<TItem> = { items: TItem[]; nextCursor: string | null };

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export const sortDirectionSchema = z.enum(SORT_DIRECTIONS);
export type SortDirection = z.infer<typeof sortDirectionSchema>;

/** A list's sort: one of the fields its owner can order by, and a direction. */
export function sortSchema<const TFields extends readonly [string, ...string[]]>(fields: TFields) {
  return z.object({ field: z.enum(fields), direction: sortDirectionSchema });
}

/** A set of ids one owner hands another to narrow its page by facts only the first one holds
 * (ADR-033 point 3): `only` these ids, or every id `except` these. Billing answers the plan and
 * status filter this way and organization pages over it, so neither reads the other's table. */
export const idSelectionSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("only"), ids: z.array(z.string()) }),
  z.object({ mode: z.literal("except"), ids: z.array(z.string()) }),
]);
export type IdSelection = z.infer<typeof idSelectionSchema>;

/** Whether `id` is inside the selection; no selection selects everything. */
export function isSelected(selection: IdSelection | undefined, id: string): boolean {
  if (!selection) return true;
  return selection.ids.includes(id) === (selection.mode === "only");
}

/** Sorts before every printable character, so "ada" + id always comes before "adam" + id. */
export const KEY_SEPARATOR = "\u0001";

/** Joins a sort field's text and an id into one key. */
export function orderKey(...parts: readonly string[]): string {
  return parts.join(KEY_SEPARATOR);
}

/** A count as text that sorts like the number: zero-padded to twelve digits. */
export function countKey(count: number): string {
  return String(Math.max(0, Math.trunc(count))).padStart(12, "0");
}

// base64url over UTF-8, written out because this package runs in the browser and in Node with
// neither's globals typed (zod only): `encodeURIComponent` gives the UTF-8 bytes, `%XX` each.
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

function utf8Bytes(text: string): number[] {
  const escaped = encodeURIComponent(text);
  const bytes: number[] = [];
  for (let index = 0; index < escaped.length; index += 1) {
    if (escaped[index] === "%") {
      bytes.push(Number.parseInt(escaped.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(escaped.charCodeAt(index));
    }
  }
  return bytes;
}

export function encodeCursor(key: string): string {
  const bytes = utf8Bytes(key);
  let out = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const [a = 0, b = 0, c = 0] = [bytes[index], bytes[index + 1], bytes[index + 2]];
    const triple = (a << 16) | (b << 8) | c;
    const chars = [18, 12, 6, 0].map((shift) => ALPHABET[(triple >> shift) & 63]).join("");
    out += chars.slice(0, Math.min(4, bytes.length - index + 1));
  }
  return out;
}

/** The key a cursor carries; undefined for a cursor this code never wrote. */
export function decodeCursor(cursor: string): string | undefined {
  if (!/^[\w-]*$/.test(cursor) || cursor.length % 4 === 1) return undefined;
  const bytes: number[] = [];
  for (let index = 0; index < cursor.length; index += 4) {
    const chunk = cursor.slice(index, index + 4);
    const triple = [...chunk.padEnd(4, "A")].reduce(
      (value, char) => (value << 6) | ALPHABET.indexOf(char),
      0,
    );
    const count = chunk.length - 1;
    for (let byte = 0; byte < count; byte += 1) bytes.push((triple >> (16 - byte * 8)) & 255);
  }
  try {
    return decodeURIComponent(bytes.map((byte) => `%${byte.toString(16).padStart(2, "0")}`).join(""));
  } catch {
    return undefined;
  }
}

/** A cursor's two halves: the sort field's text and the id, split at the last separator. */
export function cursorParts(cursor: string | undefined): { sort: string; id: string } | undefined {
  const key = cursor === undefined ? undefined : decodeCursor(cursor);
  if (key === undefined) return undefined;
  const at = key.lastIndexOf(KEY_SEPARATOR);
  if (at < 0) return undefined;
  return { sort: key.slice(0, at), id: key.slice(at + 1) };
}

/** How many rows a page holds: the asked limit, kept within bounds, or the default. */
export function pageSize(limit: number | undefined): number {
  if (limit === undefined) return DEFAULT_PAGE_SIZE;
  return Math.min(MAX_PAGE_SIZE, Math.max(1, Math.trunc(limit)));
}

export type PageOrder<TItem> = {
  /** A unique, stable key per item (`orderKey(sortText, id)`), compared byte-wise. */
  readonly keyOf: (item: TItem) => string;
  readonly direction: SortDirection;
};

function compare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** The page after `request.cursor` (the first page without one), in the list's own order: the
 * engine a memory twin pages with, and the one its Postgres twin's `keysetClauses` must match. */
export function keysetPage<TItem>(
  items: readonly TItem[],
  order: PageOrder<TItem>,
  request: KeysetPageInput,
): KeysetPage<TItem> {
  const sign = order.direction === "asc" ? 1 : -1;
  const keyed = items
    .map((item) => ({ item, key: order.keyOf(item) }))
    .toSorted((a, b) => sign * compare(a.key, b.key));
  const cursor = request.cursor === undefined ? undefined : decodeCursor(request.cursor);
  const after = cursor === undefined ? keyed : keyed.filter((entry) => sign * compare(entry.key, cursor) > 0);
  const size = pageSize(request.limit);
  const served = after.slice(0, size);
  const last = served.at(-1);
  return {
    items: served.map((entry) => entry.item),
    nextCursor: after.length > size && last ? encodeCursor(last.key) : null,
  };
}
